import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "hooks/useAuth";
import { shouldRetryRequest } from "api/apiError";
import {
  AccountUpdateInput,
  billingKeys,
  changeScheduleStatus,
  createInvoice,
  createSchedule,
  extendInvoiceDueDate,
  fetchInvoice,
  fetchInvoices,
  fetchPayments,
  fetchTenantBilling,
  Invoice,
  InvoiceCreateInput,
  InvoiceListQuery,
  InvoiceUpdateInput,
  issueInvoice,
  ManualPaymentInput,
  pauseEnforcement,
  PaymentListQuery,
  previewSchedule,
  recordManualPayment,
  resolveInvoiceReview,
  resumeEnforcement,
  reversePayment,
  ScheduleInput,
  SchedulePreviewInput,
  ScheduleUpdateInput,
  sendInvoiceReminder,
  updateBillingAccount,
  updateInvoice,
  updateSchedule,
  voidInvoice,
} from "api/billing";
import {
  connectTenantLink,
  fetchTeleopdRoles,
  fetchTenantLink,
  LinkSettingsInput,
  revealTenantLinkKeys,
  rotateTenantLinkKeys,
  syncTenantNow,
  teleopdKeys,
  testTenantLink,
  updateTenantLink,
} from "api/teleopdLink";
import { formatDate, formatINR } from "utils/format";
import { useApiMutation } from "../hooks";
import { BillingPermissions } from "./types";

const LINK_AND_BILLING = [teleopdKeys.all, billingKeys.all];

/** Client billing page: the billing snapshot, the TeleOpd link status and the user's rights. */
const useTenantBillingData = (tenantId: string) => {
  const { hasPermission } = useAuth();
  const billingQuery = useQuery({
    queryKey: billingKeys.tenant(tenantId),
    queryFn: () => fetchTenantBilling(tenantId),
    retry: shouldRetryRequest,
  });
  const linkQuery = useQuery({
    queryKey: teleopdKeys.link(tenantId),
    queryFn: () => fetchTenantLink(tenantId),
    retry: shouldRetryRequest,
  });
  // A client locked until the production release is read-only here whatever the user's role
  // (the API refuses its writes with 409 TENANT_LOCKED too).
  const locked = Boolean(billingQuery.data && billingQuery.data.tenant.stage !== "beta");
  // Until its TeleOpd has synced once only the Connection tab is offered: connecting it is the one
  // thing to do. A link that synced before but is disabled now keeps every tab (payments and voids
  // still happen). If the link status can't load, fail closed to the Connection tab, which shows
  // the error and a retry.
  const link = linkQuery.data;
  const unconnected = linkQuery.isError || Boolean(link && (!link.linked || !link.lastSyncAt));
  const permissions = React.useMemo<BillingPermissions>(
    () => ({
      canWrite: !locked && !unconnected && hasPermission("billing", "write"),
      canApprove: !locked && !unconnected && hasPermission("billing", "approve"),
      canConfigure: !locked && hasPermission("billing", "configure"),
      canSync: !locked && hasPermission("billing", "write"),
    }),
    [hasPermission, locked, unconnected]
  );
  return { billingQuery, linkQuery, permissions, locked, unconnected };
};

export default useTenantBillingData;

// ---- Reads ------------------------------------------------------------------------------------

export const useInvoiceList = (query: InvoiceListQuery, enabled = true) =>
  useQuery({
    queryKey: billingKeys.invoices(query),
    queryFn: () => fetchInvoices(query),
    retry: shouldRetryRequest,
    keepPreviousData: true,
    enabled,
  });

export const usePaymentList = (query: PaymentListQuery) =>
  useQuery({
    queryKey: billingKeys.payments(query),
    queryFn: () => fetchPayments(query),
    retry: shouldRetryRequest,
    keepPreviousData: true,
  });

export const useInvoiceDetail = (invoiceId: string) =>
  useQuery({
    queryKey: billingKeys.invoice(invoiceId),
    queryFn: () => fetchInvoice(invoiceId),
    retry: shouldRetryRequest,
  });

/** Next due dates for the schedule editor; `input` null disables the call. */
export const useSchedulePreview = (input: SchedulePreviewInput | null) =>
  useQuery({
    queryKey: billingKeys.schedulePreview(input || { firstDueDate: "", intervalMonths: 0 }),
    queryFn: () =>
      input ? previewSchedule(input) : Promise.reject(new Error("Preview input missing")),
    enabled: Boolean(input),
    retry: false,
    keepPreviousData: true,
    staleTime: 60 * 1000,
  });

export const useTeleopdRoles = (tenantId: string, enabled: boolean) =>
  useQuery({
    queryKey: teleopdKeys.roles(tenantId),
    queryFn: () => fetchTeleopdRoles(tenantId),
    retry: false,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

// ---- Writes -----------------------------------------------------------------------------------

const issuedMessage = (invoice: Invoice) =>
  invoice.number ? `Invoice ${invoice.number} issued` : "Invoice issued";

export const useInvoiceActions = (tenantId: string) => ({
  create: useApiMutation((input: InvoiceCreateInput) => createInvoice(tenantId, input), {
    success: (invoice) =>
      invoice.status === "draft" ? "Draft invoice saved" : issuedMessage(invoice),
    toastErrors: false,
  }),
  update: useApiMutation(
    ({ invoiceId, input }: { invoiceId: string; input: InvoiceUpdateInput }) =>
      updateInvoice(invoiceId, input),
    { success: "Draft invoice updated", toastErrors: false }
  ),
  issue: useApiMutation((invoiceId: string) => issueInvoice(invoiceId), {
    success: issuedMessage,
  }),
  remind: useApiMutation((invoiceId: string) => sendInvoiceReminder(invoiceId), {
    success: (result) => `Reminder sent to ${result.recipients.join(", ")}`,
  }),
  void: useApiMutation(
    ({ invoiceId, reason }: { invoiceId: string; reason: string }) =>
      voidInvoice(invoiceId, reason),
    { success: "Invoice voided" }
  ),
  extend: useApiMutation(
    ({ invoiceId, dueDate, reason }: { invoiceId: string; dueDate: string; reason: string }) =>
      extendInvoiceDueDate(invoiceId, { dueDate, reason }),
    { success: (invoice) => `Due date moved to ${formatDate(invoice.dueDate)}` }
  ),
  resolveReview: useApiMutation(
    ({ invoiceId, reason }: { invoiceId: string; reason: string }) =>
      resolveInvoiceReview(invoiceId, reason),
    { success: "Review flag cleared" }
  ),
});

export const usePaymentActions = () => ({
  record: useApiMutation(
    ({ invoiceId, input }: { invoiceId: string; input: ManualPaymentInput }) =>
      recordManualPayment(invoiceId, input),
    {
      success: (result) =>
        `Payment of ${formatINR(result.payment.amount)} recorded${
          result.payment.needsReview ? " and flagged for review" : ""
        }`,
      toastErrors: false,
    }
  ),
  reverse: useApiMutation(
    ({ paymentId, reason }: { paymentId: string; reason: string }) =>
      reversePayment(paymentId, reason),
    { success: (result) => `Payment of ${formatINR(result.payment.amount)} reversed` }
  ),
});

export const useScheduleActions = (tenantId: string) => ({
  create: useApiMutation((input: ScheduleInput) => createSchedule(tenantId, input), {
    // Periods whose invoice date has already passed are issued on save; say so, because a
    // charge with no later dues also ends right away and drops into "Show ended".
    success: (schedule) => {
      const issued = schedule.nextPeriodIndex;
      if (issued === 0) {
        return "Recurring charge added";
      }
      const what =
        issued === 1 ? "Its first invoice was issued now" : `${issued} invoices were issued now`;
      return schedule.status === "ended"
        ? `Recurring charge added. ${what}; there are no later due dates, so the charge has ended.`
        : `Recurring charge added. ${what} because the invoice date has passed.`;
    },
    toastErrors: false,
  }),
  update: useApiMutation(
    ({ scheduleId, input }: { scheduleId: string; input: ScheduleUpdateInput }) =>
      updateSchedule(scheduleId, input),
    { success: "Recurring charge updated", toastErrors: false }
  ),
  changeStatus: useApiMutation(
    ({ scheduleId, action }: { scheduleId: string; action: "pause" | "resume" | "end" }) =>
      changeScheduleStatus(scheduleId, action),
    {
      success: (_schedule, { action }) =>
        action === "pause"
          ? "Recurring charge paused"
          : action === "resume"
          ? "Recurring charge resumed"
          : "Recurring charge ended",
    }
  ),
});

export const useAccountActions = (tenantId: string) => ({
  update: useApiMutation((input: AccountUpdateInput) => updateBillingAccount(tenantId, input), {
    success: "Billing rules saved",
    toastErrors: false,
  }),
  pause: useApiMutation(
    (input: { pausedUntil: string; reason: string }) => pauseEnforcement(tenantId, input),
    {
      success: (account) =>
        account.override
          ? `Enforcement paused through ${formatDate(account.override.pausedUntil)}`
          : "Enforcement paused",
    }
  ),
  resume: useApiMutation((reason: string) => resumeEnforcement(tenantId, reason), {
    success: "Enforcement resumed",
  }),
});

export const useLinkActions = (tenantId: string) => ({
  connect: useApiMutation(
    (input: { baseUrl?: string | null; allowPrivateNetwork?: boolean }) =>
      connectTenantLink(tenantId, input),
    { success: "TeleOpd link created", invalidate: LINK_AND_BILLING }
  ),
  reveal: useApiMutation((_variables: void) => revealTenantLinkKeys(tenantId), {
    invalidate: [teleopdKeys.link(tenantId)],
  }),
  rotate: useApiMutation(
    (previousValidDays: number) => rotateTenantLinkKeys(tenantId, previousValidDays),
    { success: "Keys rotated", invalidate: LINK_AND_BILLING }
  ),
  update: useApiMutation((input: LinkSettingsInput) => updateTenantLink(tenantId, input), {
    success: (_status, input) =>
      input.enabled === false
        ? "TeleOpd link disabled"
        : input.enabled === true
        ? "TeleOpd link enabled"
        : "Connection settings saved",
    invalidate: LINK_AND_BILLING,
    toastErrors: false,
  }),
  test: useApiMutation((_variables: void) => testTenantLink(tenantId), {
    toastErrors: false,
    invalidate: [teleopdKeys.link(tenantId)],
  }),
  syncNow: useApiMutation((_variables: void) => syncTenantNow(tenantId), {
    invalidate: LINK_AND_BILLING,
  }),
});
